import { useEffect, useState } from 'react';
import { fetchClient, patchClient } from './api';
import { ArspakkerEditor } from './ArspakkerEditor';

const INPUT_CLS =
  'rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

import type { AdminClientFull, ClientStatus, UserRow } from './types';
import { STATUS_LABEL } from './types';

export function ClientDrawer({
  clientId,
  users,
  onClose,
  onSaved,
}: {
  clientId: string | null;
  users: UserRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<AdminClientFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Draft state mirrors the editable fields.
  const [draft, setDraft] = useState<{
    name: string;
    vatNumber: string;
    invoiceEmail: string;
    address: string;
    paymentTermsDays: string;
    hourlyRate: string;
    defaultSalesRepId: string;
    status: ClientStatus;
    trustpilotReviewReceived: boolean;
    cancellationNoticeMonths: string;
    notes: string;
  } | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const c = await fetchClient(clientId);
        if (cancelled) return;
        setData(c);
        setDraft({
          name: c.name,
          vatNumber: c.vatNumber ?? '',
          invoiceEmail: c.invoiceEmail ?? '',
          address: c.address ?? '',
          paymentTermsDays: String(c.paymentTermsDays),
          hourlyRate: c.hourlyRate ?? '',
          defaultSalesRepId: c.defaultSalesRepId ?? '',
          status: c.status,
          trustpilotReviewReceived: c.trustpilotReviewReceived ?? false,
          cancellationNoticeMonths: String(c.cancellationNoticeMonths ?? 3),
          notes: c.notes ?? '',
        });
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : 'Kunne ikke hente kunde');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [clientId, onClose]);

  async function save() {
    if (!data || !draft) return;
    setSaving(true);
    setErr(null);
    try {
      const patch: Parameters<typeof patchClient>[1] = {};
      if (draft.name !== data.name) patch.name = draft.name;
      if (data.vatNumber === null && draft.vatNumber !== '') patch.vat_number = draft.vatNumber;
      const email = draft.invoiceEmail.trim() === '' ? null : draft.invoiceEmail;
      if (email !== data.invoiceEmail) patch.invoice_email = email;
      const addr = draft.address.trim() === '' ? null : draft.address;
      if (addr !== data.address) patch.address = addr;
      const ptd = Number(draft.paymentTermsDays);
      if (Number.isFinite(ptd) && ptd !== data.paymentTermsDays) patch.payment_terms_days = ptd;
      const hr = draft.hourlyRate.trim() === '' ? null : draft.hourlyRate;
      if (hr !== data.hourlyRate) patch.hourly_rate = hr;
      const rep = draft.defaultSalesRepId === '' ? null : draft.defaultSalesRepId;
      if (rep !== data.defaultSalesRepId) patch.default_sales_rep_id = rep;
      if (draft.status !== data.status) patch.status = draft.status;
      if (draft.trustpilotReviewReceived !== data.trustpilotReviewReceived) {
        patch.trustpilot_review_received = draft.trustpilotReviewReceived;
      }
      const cnm = Number(draft.cancellationNoticeMonths);
      if (Number.isInteger(cnm) && cnm >= 0 && cnm <= 24 && cnm !== data.cancellationNoticeMonths) {
        patch.cancellation_notice_months = cnm;
      }
      const notes = draft.notes.trim() === '' ? null : draft.notes;
      if (notes !== data.notes) patch.notes = notes;

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      await patchClient(data.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke gemme kunde');
    } finally {
      setSaving(false);
    }
  }

  if (!clientId) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Luk"
        className="absolute inset-0 bg-ink-0/20"
        onClick={onClose}
      />
      <aside className="absolute right-0 top-0 h-full w-[560px] max-w-full overflow-y-auto bg-paper-0 shadow-xl">
        {loading ? (
          <div className="p-6 text-sm text-ink-2">Henter kunde…</div>
        ) : err && !draft ? (
          <div className="p-6 text-sm text-status-err">{err}</div>
        ) : data && draft ? (
          <div className="flex flex-col gap-4 p-6">
            <header className="flex items-center justify-between border-b border-line-1 pb-3">
              <h2 className="text-lg font-semibold text-primary">Rediger kunde</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Luk"
                className="rounded p-1 text-ink-2 hover:bg-paper-2"
              >
                ✕
              </button>
            </header>

            <Field label="Navn">
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>

            <Field label="CVR">
              {data.vatNumber === null ? (
                <input
                  type="text"
                  value={draft.vatNumber}
                  onChange={(e) => setDraft({ ...draft, vatNumber: e.target.value })}
                  className={INPUT_CLS}
                  placeholder="DK12345678"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span className="flex-1 rounded border border-line-1 bg-paper-1 px-2 py-1 text-sm text-ink-1">
                    {data.vatNumber}
                  </span>
                  <span className="text-xs text-ink-2">Kontakt admin for VAT-ændring</span>
                </div>
              )}
            </Field>

            <Field label="Faktura-email">
              <input
                type="email"
                value={draft.invoiceEmail}
                onChange={(e) => setDraft({ ...draft, invoiceEmail: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>

            <Field label="Adresse">
              <textarea
                value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                className={`${INPUT_CLS} h-20`}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Betalingsbetingelser (dage)">
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={draft.paymentTermsDays}
                  onChange={(e) => setDraft({ ...draft, paymentTermsDays: e.target.value })}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Timerate (kr/t)">
                <input
                  type="text"
                  inputMode="decimal"
                  value={draft.hourlyRate}
                  onChange={(e) => setDraft({ ...draft, hourlyRate: e.target.value })}
                  className={INPUT_CLS}
                  placeholder="950.00"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Ansvarlig">
                <select
                  value={draft.defaultSalesRepId}
                  onChange={(e) => setDraft({ ...draft, defaultSalesRepId: e.target.value })}
                  className={INPUT_CLS}
                >
                  <option value="">—</option>
                  {users
                    .filter((u) => u.active)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value as ClientStatus })}
                  className={INPUT_CLS}
                >
                  {(['active', 'paused', 'churned'] as ClientStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink-1">
              <input
                type="checkbox"
                checked={draft.trustpilotReviewReceived}
                onChange={(e) => setDraft({ ...draft, trustpilotReviewReceived: e.target.checked })}
              />
              Trustpilot review modtaget
            </label>

            <Field label="Opsigelsesvarsel (måneder)">
              <input
                type="number"
                min={0}
                max={24}
                step={1}
                value={draft.cancellationNoticeMonths}
                onChange={(e) => setDraft({ ...draft, cancellationNoticeMonths: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>

            <Field label="Noter">
              <textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className={`${INPUT_CLS} h-24`}
              />
            </Field>

            <ArspakkerEditor clientId={data.id} />

            {err ? <p className="text-sm text-status-err">{err}</p> : null}

            <footer className="flex justify-end gap-2 border-t border-line-1 pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded border border-line-2 px-3 py-1 text-sm text-ink-1 hover:border-line-2"
              >
                Annullér
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded bg-accent px-3 py-1 text-sm text-white disabled:opacity-50"
              >
                {saving ? 'Gemmer…' : 'Gem'}
              </button>
            </footer>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-ink-1">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-2">{label}</span>
      {children}
    </label>
  );
}
